import React, { useState, useEffect } from "react";
import axios from "../../../api/api";
import toast from "react-hot-toast";
import LazyLoading from "../../../components/LazyLoad";
import { FaHistory } from "react-icons/fa";

function AppVersionManager() {
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("ios");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState({
    current: {},
    history: [],
  });
  const [versionData, setVersionData] = useState({
    latestVersion: "",
    minRequiredVersion: "",
    updateMessage: "",
    updateUrl: "",
    updateRequired: false,
  });

  // Fetch existing version data
  useEffect(() => {
    const fetchVersions = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/app-version");
        console.log("response:", response);
        if (response.data.result === "success") {
          const versions = response.data.data;
          setPlatforms(versions);

          // Set initial version data if available
          const platformData = versions.find(
            (v) => v.platform === selectedPlatform
          );
          if (platformData) {
            setVersionData({
              latestVersion: platformData.latestVersion || "",
              minRequiredVersion: platformData.minRequiredVersion || "",
              updateMessage: platformData.updateMessage || "",
              updateUrl: platformData.updateUrl || "",
              updateRequired: platformData.updateRequired || false,
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch version data:", error);
        toast.error("Failed to fetch version data");
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [selectedPlatform]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post("/app-version", {
        platform: selectedPlatform,
        ...versionData,
      });

      if (response.data.result === "success") {
        toast.success("Version updated successfully!");
        // If history is shown, refresh it
        if (showHistory) {
          fetchVersionHistory();
        }
      }
    } catch (error) {
      console.error("Failed to update version:", error);
      toast.error("Failed to update version. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch version history
  const fetchVersionHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `/app-version/history/${selectedPlatform}`
      );

      if (response.data.result === "success") {
        setVersionHistory(response.data.data);
      } else {
        toast.error(response.data.message || "Failed to load version history");
      }
    } catch (error) {
      console.error("Failed to fetch version history:", error);
      toast.error("Failed to load version history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Toggle history view
  const toggleHistory = () => {
    if (!showHistory) {
      fetchVersionHistory();
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <div className="p-3 sm:p-5">
      <div className="lg:ml-64">
        <div className="rounded-lg dark:border-gray-700 lg:mt-16 mt-0">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-gray-900 dark:text-gray-800 text-2xl font-bold">
              App Version Manager
            </h3>
            <button
              onClick={toggleHistory}
              className="flex items-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded"
              disabled={historyLoading}
            >
              <FaHistory className="mr-2" />
              {showHistory ? "Hide History" : "View History"}
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            {loading ? (
              <LazyLoading />
            ) : (
              <>
                <div className="flex mb-6">
                  <button
                    className={`px-4 py-2 mr-2 rounded-tl-lg rounded-tr-lg ${
                      selectedPlatform === "ios"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => {
                      setSelectedPlatform("ios");
                      setShowHistory(false);
                    }}
                  >
                    iOS
                  </button>
                  <button
                    className={`px-4 py-2 rounded-tl-lg rounded-tr-lg ${
                      selectedPlatform === "android"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => {
                      setSelectedPlatform("android");
                      setShowHistory(false);
                    }}
                  >
                    Android
                  </button>
                </div>

                {showHistory ? (
                  historyLoading ? (
                    <LazyLoading />
                  ) : (
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <h4 className="text-lg font-semibold mb-4">
                        Version History
                      </h4>

                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-100 p-4 border-b">
                          <h5 className="font-medium">Current Version</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                            <div>
                              <span className="block text-sm text-gray-500">
                                Latest Version:
                              </span>
                              <span className="font-medium">
                                {versionHistory.current?.latestVersion}
                              </span>
                            </div>
                            <div>
                              <span className="block text-sm text-gray-500">
                                Minimum Required:
                              </span>
                              <span className="font-medium">
                                {versionHistory.current?.minRequiredVersion}
                              </span>
                            </div>
                            <div>
                              <span className="block text-sm text-gray-500">
                                Force Update:
                              </span>
                              <span className="font-medium">
                                {versionHistory.current?.updateRequired
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                            <div className="md:col-span-3">
                              <span className="block text-sm text-gray-500">
                                Message:
                              </span>
                              <span className="italic">
                                {versionHistory.current?.updateMessage}
                              </span>
                            </div>
                            <div className="md:col-span-3">
                              <span className="block text-sm text-gray-500">
                                Updated:
                              </span>
                              <span>
                                {formatDate(versionHistory.current?.updatedAt)}
                              </span>
                            </div>
                            {versionHistory.current?.updatedBy && (
                              <div className="md:col-span-3">
                                <span className="block text-sm text-gray-500">
                                  Updated By:
                                </span>
                                <span>
                                  {versionHistory.current.updatedBy.fullName ||
                                    "Unknown"}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="divide-y">
                          {versionHistory.history &&
                          versionHistory.history.length > 0 ? (
                            versionHistory.history.map((entry, index) => (
                              <div key={index} className="p-4 hover:bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <span className="block text-sm text-gray-500">
                                      Latest Version:
                                    </span>
                                    <span className="font-medium">
                                      {entry.latestVersion}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="block text-sm text-gray-500">
                                      Minimum Required:
                                    </span>
                                    <span className="font-medium">
                                      {entry.minRequiredVersion}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="block text-sm text-gray-500">
                                      Force Update:
                                    </span>
                                    <span className="font-medium">
                                      {entry.updateRequired ? "Yes" : "No"}
                                    </span>
                                  </div>
                                  <div className="md:col-span-3">
                                    <span className="block text-sm text-gray-500">
                                      Message:
                                    </span>
                                    <span className="italic">
                                      {entry.updateMessage}
                                    </span>
                                  </div>
                                  <div className="md:col-span-3">
                                    <span className="block text-sm text-gray-500">
                                      Updated:
                                    </span>
                                    <span>{formatDate(entry.timestamp)}</span>
                                  </div>
                                  {entry.updatedBy && (
                                    <div className="md:col-span-3">
                                      <span className="block text-sm text-gray-500">
                                        Updated By:
                                      </span>
                                      <span>
                                        {entry.updatedBy.fullName || "Unknown"}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500">
                              No version history available
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => setShowHistory(false)}
                        className="mt-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg text-sm px-5 py-2.5"
                      >
                        Back to Editor
                      </button>
                    </div>
                  )
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Latest Version
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., 1.2.0"
                          value={versionData.latestVersion}
                          onChange={(e) =>
                            setVersionData({
                              ...versionData,
                              latestVersion: e.target.value,
                            })
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Minimum Required Version
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., 1.0.0"
                          value={versionData.minRequiredVersion}
                          onChange={(e) =>
                            setVersionData({
                              ...versionData,
                              minRequiredVersion: e.target.value,
                            })
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Update Message
                      </label>
                      <textarea
                        placeholder="Message to show users when an update is required"
                        value={versionData.updateMessage}
                        onChange={(e) =>
                          setVersionData({
                            ...versionData,
                            updateMessage: e.target.value,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded-md h-24"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Store URL
                      </label>
                      <input
                        type="url"
                        placeholder="App Store or Play Store URL"
                        value={versionData.updateUrl}
                        onChange={(e) =>
                          setVersionData({
                            ...versionData,
                            updateUrl: e.target.value,
                          })
                        }
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        id="updateRequired"
                        type="checkbox"
                        checked={versionData.updateRequired}
                        onChange={(e) =>
                          setVersionData({
                            ...versionData,
                            updateRequired: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="updateRequired"
                        className="ml-2 block text-sm text-gray-700"
                      >
                        Force Update (All users must update regardless of
                        version)
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg text-sm px-5 py-2.5 focus:ring-4 focus:outline-none focus:ring-blue-300"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Version Settings"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppVersionManager;
